import { BoxMargin } from 'tabler-icons-react';
import {
  ECOM_ENTITY_TYPES,
  ECOM_WEBSTORE_SECTION_BOUND_TYPE,
  ECOM_WEBSTORE_SECTION_TYPE_ENUM,
  HEADING_SIZE_STYLE_ENUM,
  INPUT_CONTROL_TYPE_ENUM
} from '../../../../../config/constants';
import WsCategoryGrid from './WsCategoryGrid';

const LIST_DISPLAY_ENUM = {
  TEXT: 'text',
  CARD: 'card'
};

const LIST_SORT_ENUM = {
  ALPHABETICAL: 'alphabetical',
  ALPHABETICAL_TOP_DOWN: 'alphabetical_top_down',
  PRODUCT_COUNT: 'product_count'
};

const SORT_BY_ENUM = {
  NAME: 'name',
  NAME_DESC: 'name_desc',
  DATE: 'price',
  DATE_DESC: 'price_desc',
  COUNT: 'count',
  COUNT_DESC: 'count_desc'
};

const CARD_SIZE_ENUM = {
  EXTRA_SMALL: 'extra_small',
  SMALL: 'small',
  MEDIUM: 'medium',
  LARGE: 'large',
  EXTRA_LARGE: 'extra_large',
  HUGE: 'huge'
};

const configNameValues = {
  headingText: 'headingText',
  subCategoryHeadingText: 'subCategoryHeadingText',
  headingSize: 'headingSize',
  displayType: 'displayType',
  isOutlineStyle: 'isOutlineStyle',
  showIcons: 'showIcons',
  cardSize: 'cardSize',
  textSortBy: 'textSortBy',
  textAlign: 'textAlign',
  iconPosition: 'iconPosition',
  fontSize: 'fontSize',
  desktopColumnCount: 'desktopColumnCount',
  categoriesFullWidth: 'categoriesFullWidth',
  colorScheme: 'colorScheme',
  mobileColumnCount: 'mobileColumnCount'
};

const configDefaultValues = {
  [configNameValues.headingText]: 'Categories',
  [configNameValues.subCategoryHeadingText]: 'Subcategories',
  [configNameValues.headingSize]: HEADING_SIZE_STYLE_ENUM.MEDIUM,
  [configNameValues.displayType]: LIST_DISPLAY_ENUM.CARD,
  [configNameValues.isOutlineStyle]: 'true',
  [configNameValues.showIcons]: 'true',
  [configNameValues.cardSize]: CARD_SIZE_ENUM.SMALL,
  [configNameValues.textSortBy]: LIST_SORT_ENUM.ALPHABETICAL,
  [configNameValues.fontSize]: '18',
  [configNameValues.textAlign]: 'left',
  [configNameValues.iconPosition]: 'top',
  [configNameValues.desktopColumnCount]: '4',
  [configNameValues.categoriesFullWidth]: 'false',
  [configNameValues.colorScheme]: '1',
  [configNameValues.mobileColumnCount]: '2'
};

const configOptions = {
  [configNameValues.headingText]: {
    type: INPUT_CONTROL_TYPE_ENUM.TEXT_INPUT,
    isTextArea: true,
    label: 'Heading'
  },
  [configNameValues.subCategoryHeadingText]: {
    type: INPUT_CONTROL_TYPE_ENUM.TEXT_INPUT,
    isTextArea: true,
    label: 'Subcategory Heading'
  },
  [configNameValues.headingSize]: {
    label: 'Heading size',
    type: INPUT_CONTROL_TYPE_ENUM.DROP_DOWN,
    options: [
      {
        value: HEADING_SIZE_STYLE_ENUM.SMALL,
        label: 'Small'
      },
      {
        value: HEADING_SIZE_STYLE_ENUM.MEDIUM,
        label: 'Medium'
      },
      {
        value: HEADING_SIZE_STYLE_ENUM.LARGE,
        label: 'Large'
      },
      {
        value: HEADING_SIZE_STYLE_ENUM.EXTRA_LARGE,
        label: 'Extra large'
      },
      {
        value: HEADING_SIZE_STYLE_ENUM.HUGE,
        label: 'Huge'
      }
    ]
  },
  [configNameValues.desktopColumnCount]: {
    label: 'Desktop column count',
    type: INPUT_CONTROL_TYPE_ENUM.SLIDER,
    min: 1,
    max: 6,
    unit: ''
  },
  [configNameValues.displayType]: {
    type: INPUT_CONTROL_TYPE_ENUM.BUTTON_GROUP,
    label: 'Display type',
    options: [
      {
        value: LIST_DISPLAY_ENUM.TEXT,
        label: 'Text'
      },
      {
        value: LIST_DISPLAY_ENUM.CARD,
        label: 'Card'
      }
    ]
  },
  [configNameValues.isOutlineStyle]: {
    type: INPUT_CONTROL_TYPE_ENUM.CHECKBOX,
    label: 'Outline button style',
    isVisible: (config) =>
      config[configNameValues.displayType] === LIST_DISPLAY_ENUM.CARD
  },
  [configNameValues.showIcons]: {
    type: INPUT_CONTROL_TYPE_ENUM.CHECKBOX,
    label: 'Show icons',
    isVisible: (config) =>
      config[configNameValues.displayType] === LIST_DISPLAY_ENUM.CARD
  },
  [configNameValues.cardSize]: {
    type: INPUT_CONTROL_TYPE_ENUM.DROP_DOWN,
    label: 'Card size',
    options: [
      {
        value: 'extra_small',
        label: 'Extra Small'
      },
      {
        value: 'small',
        label: 'Small'
      },
      {
        value: 'medium',
        label: 'Medium'
      },
      {
        value: 'large',
        label: 'Large'
      },
      {
        value: 'extra_large',
        label: 'Extra Large'
      },
      {
        value: 'huge',
        label: 'Huge'
      }
    ],
    isVisible: (config) =>
      config[configNameValues.displayType] === LIST_DISPLAY_ENUM.CARD
  },
  [configNameValues.textSortBy]: {
    type: INPUT_CONTROL_TYPE_ENUM.DROP_DOWN,
    label: 'Sort by',
    options: [
      {
        value: LIST_SORT_ENUM.ALPHABETICAL,
        label: 'Alphabetical'
      },
      {
        value: LIST_SORT_ENUM.ALPHABETICAL_TOP_DOWN,
        label: 'Alphabetical (Top Down)'
      },
      {
        value: LIST_SORT_ENUM.PRODUCT_COUNT,
        label: 'Product Count'
      }
    ]
  },
  [configNameValues.textAlign]: {
    type: INPUT_CONTROL_TYPE_ENUM.DROP_DOWN,
    label: 'Text alignment',
    options: [
      {
        value: 'left',
        label: 'Left'
      },
      {
        value: 'center',
        label: 'Center'
      },
      {
        value: 'right',
        label: 'Right'
      }
    ]
  },
  [configNameValues.iconPosition]: {
    type: INPUT_CONTROL_TYPE_ENUM.DROP_DOWN,
    label: 'Icon position',
    options: [
      {
        value: 'top',
        label: 'Top'
      },
      {
        value: 'bottom',
        label: 'Bottom'
      }
    ],
    isVisible: (config) =>
      config[configNameValues.displayType] === LIST_DISPLAY_ENUM.CARD &&
      config[configNameValues.showIcons] === 'true' &&
      config[configNameValues.textAlign] === 'center'
  },
  [configNameValues.fontSize]: {
    label: 'Text size',
    type: INPUT_CONTROL_TYPE_ENUM.SLIDER,
    min: 10,
    max: 18,
    unit: 'px'
  },
  [configNameValues.categoriesFullWidth]: {
    label: 'Stretch categories full width',
    type: INPUT_CONTROL_TYPE_ENUM.CHECKBOX
  },
  [configNameValues.colorScheme]: {
    label: 'Color scheme',
    type: 'entity',
    entityType: ECOM_ENTITY_TYPES.ecomStoreThemeColorScheme
  },
  [configNameValues.mobileColumnCount]: {
    type: INPUT_CONTROL_TYPE_ENUM.BUTTON_GROUP,
    label: 'Mobile column count',
    category: 'Mobile layout',
    options: [
      {
        value: '1',
        label: '1 column'
      },
      {
        value: '2',
        label: '2 columns'
      }
    ]
  }
};

const config = {
  type: ECOM_WEBSTORE_SECTION_TYPE_ENUM.CATEGORY_GRID,
  title: 'Category grid',
  icon: BoxMargin,
  allowedBounds: [ECOM_WEBSTORE_SECTION_BOUND_TYPE.BODY],
  Component: WsCategoryGrid,
  configOptions,
  isPermanent: true,
  cannotCreate: true,
  maxItemCount: null,
  blocks: [],
  defaultConfig: configDefaultValues
};

export {
  config,
  configOptions,
  configNameValues,
  configDefaultValues,
  LIST_DISPLAY_ENUM,
  SORT_BY_ENUM,
  LIST_SORT_ENUM
};
